/**
 * T-146-001 + T-146-002 — tenant service-call allowance widget (dispatch
 * ORCHESTRATOR 2026-10-07T08:00Z, T-146 ASSIGNED).
 *
 * T-146-001: total allowance, used calls, remaining calls and overage rate
 * for the current plan, from GET api/subscription/current
 * ({success, data: {tier, status, allowance, remaining, resetDate}} —
 * LP subscription-manager.js getCurrentPlan). Envelope unwrap matches
 * gates/evidence-safe/guard.ts fetchTenantTier.
 *
 * Overage rate is derived as used / allowance * 100 (used = allowance -
 * remaining). The LP payload carries NO monetary per-unit rate and
 * usd-pricing.js perUnitRate is admin-configurable (F-173) defaulting to 0,
 * so a hardcoded $/call figure in FE would be false — the tile shows the
 * percentage of allowance consumed instead (null → em dash when the plan
 * is not metered, allowance <= 0).
 *
 * T-146-002 remainder (US-146, dispatch 233b419 2026-10-07T17:05Z, with the
 * human T-242 unblock): the upgrade CTA stays a link (/pricing?plan=<next>
 * — the route is now live per T-242), while the top-up CTA OPENS the
 * FastSpring popup store flow directly with the tier's per-unit SKU built
 * CLIENT-SIDE from the usd-catalog GETs — LP usd-catalog.js carries no
 * checkout endpoint (verified on LP main, dispatch 233b419), so the payload
 * is assembled from the F-175 catalog paths and opened through
 * services/fastspringStorefront.ts (loadUs2Storefront + attach/open).
 * After a completed popup order and on window refocus the widget re-fetches
 * GET api/subscription/current so the four tiles update without reload.
 * Store-unbaked / wrong-market builds fail closed: the top-up CTA stays the
 * v1 /pricing?plan=<tier>&topup=1 link (never inventing a store id).
 *
 * Per-unit pricing display: the LP allowance payload carries no monetary
 * rate, but the F-175 catalog does (kind 'overage', price = perUnitRate).
 * The figure surfaces from the catalog payload when the top-up popup cart
 * is built (best-effort; absent ⇒ no $ figure — never a fabricated one);
 * the popup path falls back to the deterministic texas-overage-<tier>.
 *
 * States: loading / error (401, 403, generic) / empty (no data or not
 * metered) / loaded. Amber warning when remaining < 20% of allowance.
 *
 * US-147 (dispatch f9222ce 2026-10-08T10:45Z) rides on this machinery:
 * T-147-001 — an exhaustion prompt AT the hard block (100% used): per-use
 * rate from the same catalog GET, quantity selector, total cost shown
 * before purchase. T-147-002 — the popup cart carries the chosen quantity
 * (was hardcoded 1) and tags.tenantId/email so the LP webhook can
 * attribute the one-off order for the receipt email (order.completed →
 * sendOverageReceiptEmail, LP half of this dispatch).
 *
 * FastSpring popup close protocol (us2 SBL): a completed order is signalled
 * via window.fastspring.builder.currentOrder() + a session close; the popup
 * closing AFTER an explicit user Cancel carries cancelled=true and invokes
 * NO callback; the window focus that returns when the popup closes re-arms
 * the CTA (a 'popup' state never survives a refocus) and the refetch keeps
 * the tiles honest either way. The REFETCH_event window event lets the
 * completion branch trigger the same refresh as focus refetch.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { adminApi, landlordApi } from '../../../services/api';
import {
  attachPopupSession,
  loadUs2Storefront,
  openPopupSession,
  resolveStorefront,
} from '../../../services/fastspringStorefront';

export interface AllowanceData {
    tier: string;
    status?: string;
    allowance: number;
    remaining: number;
    resetDate?: string;
}

export type AllowancePhase = 'loading' | 'error' | 'empty' | 'loaded';

export const LOW_REMAINING_THRESHOLD = 0.2;

/** Tier ladder mirrored from LP config/tiers.js PLAN_ORDER (FE display only). */
export const PLAN_ORDER = ['freemium', 'essentials', 'professional', 'enterprise'];

export function nextTier(tier: string | null | undefined): string | null {
    if (!tier) return null;
    const idx = PLAN_ORDER.indexOf(String(tier).toLowerCase());
    if (idx === -1 || idx >= PLAN_ORDER.length - 1) return null;
    return PLAN_ORDER[idx + 1];
}

/** T-146-002: links only, same /pricing?plan= pattern as evidence-safe CTA. */
export function topUpHref(tier: string): string {
    return `/pricing?plan=${encodeURIComponent(tier)}&topup=1`;
}

export function upgradeHref(next: string): string {
    return `/pricing?plan=${encodeURIComponent(next)}&upgrade=1`;
}

/** Unwrap the axios envelope; null when the payload is absent or not metered. */
export function parseAllowance(res: any): AllowanceData | null {
    const raw = res?.data?.data ?? res?.data ?? {};
    const allowance = Number(raw?.allowance);
    const remaining = Number(raw?.remaining);
    if (!Number.isFinite(allowance) || allowance <= 0) return null;
    if (!Number.isFinite(remaining)) return null;
    return {
        tier: raw?.tier == null ? '' : String(raw.tier),
        status: raw?.status == null ? undefined : String(raw.status),
        allowance,
        remaining: Math.max(remaining, 0),
        resetDate: raw?.resetDate == null ? undefined : String(raw.resetDate),
    };
}

export function usedCalls(d: AllowanceData): number {
    return Math.max(d.allowance - d.remaining, 0);
}

/** Overage rate = share of allowance consumed (%). Null when not metered. */
export function overageRatePct(d: AllowanceData): number | null {
    if (d.allowance <= 0) return null;
    return (usedCalls(d) / d.allowance) * 100;
}

/**
 * Per-unit top-up SKU lookup (F-175). Returns the catalog product for
 * kind 'overage' on the tier, or null when the catalog has none. Callers
 * fall back to the deterministic path texas-overage-<tierId> when this
 * returns null — the LP sync only omits the SKU for dormant configs.
 */
export function overageProductFor(
    products: Array<Record<string, unknown>>,
    tierId: string
): Record<string, unknown> | null {
    if (!tierId) return null;
    const hit = products.find(
        (p) => p && p.kind === 'overage' && String(p.tierId) === String(tierId) && p.productPath
    );
    return hit ?? null;
}

/** Unwrap the usd-catalog response envelope ({ data: { success, data } }). */
export function unwrapEnvelope(res: any): any {
    return res?.data?.data ?? res?.data ?? null;
}

/** Amber rule: remaining STRICTLY below 20% of allowance (dispatch). */
export function isLowRemaining(d: AllowanceData): boolean {
    return d.allowance > 0 && d.remaining < d.allowance * LOW_REMAINING_THRESHOLD;
}

/** US-147 T-147-001: the hard block — 100% of the allowance used. */
export function isExhausted(d: AllowanceData): boolean {
    return d.allowance > 0 && d.remaining <= 0;
}

/** Clamp the quantity selector (T-147-001): integer 1..1000. */
export function clampTopUpQuantity(raw: number): number {
    if (!Number.isFinite(raw)) return 1;
    return Math.min(1000, Math.max(1, Math.floor(raw)));
}

/**
 * Signed-in claims from the stored Cognito token (same decode pattern as
 * ProfileTab.getTenantId). US-147 T-147-002: the popup tags carry
 * tenantId/email so the FastSpring webhook can attribute the one-off
 * order (receipt email). Garbage/absent tokens degrade to nulls — the
 * tags simply carry less, never throw.
 */
export function sessionClaims(): { tenantId: string | null; email: string | null } {
    try {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('idToken');
        if (!token) return { tenantId: null, email: null };
        const payload = JSON.parse(atob(token.split('.')[1]));
        return {
            tenantId: payload['custom:tenantId'] || null,
            email: payload.email || null,
        };
    } catch {
        return { tenantId: null, email: null };
    }
}

function errorMessage(err: any): string {
    const status = err?.response?.status;
    if (status === 401) return 'Sign in to view your service call allowance.';
    if (status === 403) return 'You do not have permission to view allowance usage.';
    return 'We could not load your allowance right now. Please try again.';
}

/** US-146: re-fetch trigger after popup completion + on window focus. */
export const REFETCH_event = 'allowance-widget-refetch';

/** Fire the widget's refetch (used after a completed popup order). */
export function dispatchAllowanceRefetch(): void {
    try {
        window.dispatchEvent(new Event(REFETCH_event));
    } catch {
        /* very old browsers: the focus listener remains the fallback */
    }
}

const AllowanceWidget: React.FC = () => {
    const [phase, setPhase] = useState<AllowancePhase>('loading');
    const [data, setData] = useState<AllowanceData | null>(null);
    const [error, setError] = useState<string>('');

    const load = useCallback(async () => {
        setPhase('loading');
        setError('');
        try {
            const res = await adminApi.get('api/subscription/current');
            const parsed = parseAllowance(res);
            if (parsed) {
                setData(parsed);
                setPhase('loaded');
            } else {
                setData(null);
                setPhase('empty');
            }
        } catch (err: any) {
            setData(null);
            setError(errorMessage(err));
            setPhase('error');
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    /** Mirrors topUpState for the focus handler (no stale closure). */
    const topUpStateRef = useRef<'idle' | 'loading' | 'popup' | 'error'>('idle');

    // US-146 AC 4-5: usage persists after a top-up and the state updates in
    // real time — re-fetch on window refocus and after an in-process popup
    // completion. The REFETCH_event window event lets the popup close
    // handler (and any post-checkout flow) trigger the same refresh without
    // prop wiring; a load already in flight is simply superseded by load()'s
    // own phase reset (last response wins, no queue).
    useEffect(() => {
        const onFocus = () => {
            if (document.visibilityState !== 'visible') return;
            // A cancelled popup fires no close callback; the focus that
            // returns when it closes re-arms the CTA (never stuck busy).
            if (topUpStateRef.current === 'popup') setTopUpState('idle');
            load();
        };
        window.addEventListener('focus', onFocus);
        window.addEventListener(REFETCH_event, load as unknown as EventListener);
        return () => {
            window.removeEventListener('focus', onFocus);
            window.removeEventListener(REFETCH_event, load as unknown as EventListener);
        };
    }, [load]);

    // ── T-146-002 remainder: top-up popup store flow ────────────────────────
    const popupReady = resolveStorefront().ok; // env store id, us2 market only
    const [topUpState, setTopUpState] = useState<
        'idle' | 'loading' | 'popup' | 'error'
    >('idle');
    useEffect(() => {
        topUpStateRef.current = topUpState;
    }, [topUpState]);
    const [topUpMsg, setTopUpMsg] = useState('');
    /** The popup cart this widget opened — marks the close as OUR session. */
    const cartRef = useRef<{ path: string; quantity: number } | null>(null);
    /** Live per-unit rate from the F-175 catalog (null hides the $ figure). */
    const [unitRateCents, setUnitRateCents] = useState<number | null>(null);
    /** US-147 T-147-001: how many extra calls the buyer wants (1..1000). */
    const [topUpQty, setTopUpQty] = useState(1);

    // US-147 T-147-001: when the allowance is EXHAUSTED the prompt shows the
    // live per-unit rate WITHOUT waiting for a checkout click (best-effort;
    // absent catalog ⇒ no $ figure — never a fabricated one).
    useEffect(() => {
        if (phase !== 'loaded' || !data || !isExhausted(data) || unitRateCents != null) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await landlordApi.get('api/usd-catalog/products');
                const catalog = unwrapEnvelope(res);
                const products: Array<Record<string, unknown>> = Array.isArray(catalog?.products)
                    ? catalog.products
                    : [];
                const overage = overageProductFor(products, data.tier);
                const price = overage ? Number(overage.price) : NaN;
                if (!cancelled && Number.isFinite(price) && price >= 0) setUnitRateCents(price);
            } catch {
                /* best-effort: the prompt degrades to a quantity-only prompt */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [phase, data, unitRateCents]);

    /**
     * Open the FastSpring popup with the tier's per-unit SKU, built
     * CLIENT-SIDE from the usd-catalog GETs (LP has no usd-catalog checkout
     * endpoint — verified on LP main, dispatch 233b419; the ruling on
     * dispatch f9222ce KEEPS this design over POST /api/usd-catalog/checkout,
     * which returns a hosted redirect URL). Path preference: live catalog
     * (kind 'overage') → deterministic texas-overage-<tier>.
     * tags.tier ties the SBL order session to the tier for the webhook;
     * US-147 T-147-002 adds tenantId/email so the order.completed webhook
     * can attribute the purchase (receipt email) — quantity now rides the
     * cart entry instead of the old hardcoded 1.
     */
    const startTopUpCheckout = useCallback(
        async (d: AllowanceData, qty: number = 1) => {
            if (topUpStateRef.current === 'loading' || topUpStateRef.current === 'popup') return;
            let card: { path: string; quantity: number } | null = null;
            setTopUpState('loading');
            try {
                await loadUs2Storefront();

                let path: string | null = null;
                let rate: number | null = null;
                try {
                    const res = await landlordApi.get('api/usd-catalog/products');
                    const catalog = unwrapEnvelope(res);
                    const products: Array<Record<string, unknown>> = Array.isArray(catalog?.products)
                        ? catalog.products
                        : [];
                    const overage = overageProductFor(products, d.tier);
                    if (overage) {
                        path = String(overage.productPath);
                        const price = Number(overage.price);
                        if (Number.isFinite(price) && price >= 0) rate = price;
                    }
                } catch {
                    /* catalog unavailable → deterministic path fallback */
                }
                if (!path) path = `texas-overage-${d.tier}`;
                if (rate != null) setUnitRateCents(rate);

                card = { path, quantity: clampTopUpQuantity(qty) };
                const claims = sessionClaims();
                const composeData = {
                    products: [card],
                    tags: {
                        tier: d.tier,
                        ...(claims.tenantId ? { tenantId: claims.tenantId } : {}),
                        ...(claims.email ? { email: claims.email } : {}),
                    },
                    contact: {},
                };
                cartRef.current = card;
                attachPopupSession({
                    onClosed: () => {
                        // FastSpring flushes the cart only on a completed
                        // order; an explicit user Cancel closes the popup
                        // with cancelled=true and invokes NO callback, so a
                        // cart-present close IS a completed purchase.
                        const completed = cartRef.current;
                        cartRef.current = null;
                        if (completed) {
                            setTopUpState('idle');
                            dispatchAllowanceRefetch(); // tiles update live
                        }
                        /* no cart ⇒ already errored; the focus handler re-arms */
                    },
                    onData: () => {
                        /* order lifecycle detail — webhook is authoritative */
                    },
                    onError: (code: string, message: string) => {
                        cartRef.current = null;
                        setTopUpMsg(message || `Payment error (${code})`);
                        setTopUpState('error');
                    },
                });

                openPopupSession(composeData);
                setTopUpState('popup');
            } catch (err: any) {
                cartRef.current = null;
                setTopUpMsg(err?.message || 'Failed to start checkout.');
                setTopUpState('error');
            }
        },
        []
    );

    if (phase === 'loading') {
        return (
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6" data-testid="allowance-loading">
                <h4 className="text-lg font-medium text-gray-900 mb-2">Service Call Allowance</h4>
                <p className="text-sm text-gray-500">
                    <i className="fas fa-spinner fa-spin"></i> Loading allowance…
                </p>
            </div>
        );
    }

    if (phase === 'error') {
        return (
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6" data-testid="allowance-error">
                <h4 className="text-lg font-medium text-gray-900 mb-2">Service Call Allowance</h4>
                <p className="text-sm text-red-600" role="alert">{error}</p>
            </div>
        );
    }

    if (phase === 'empty' || !data) {
        return (
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6" data-testid="allowance-empty">
                <h4 className="text-lg font-medium text-gray-900 mb-2">Service Call Allowance</h4>
                <p className="text-sm text-gray-500">
                    No metered service call allowance on your plan yet.
                </p>
            </div>
        );
    }

    const used = usedCalls(data);
    const rate = overageRatePct(data);
    const low = isLowRemaining(data);
    const exhausted = isExhausted(data);
    const usedPct = Math.min((used / data.allowance) * 100, 100);
    const next = nextTier(data.tier);

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6" data-testid="allowance-widget">
            <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-medium text-gray-900">Service Call Allowance</h4>
                {data.resetDate && (
                    <span className="text-xs text-gray-500" data-testid="allowance-reset">
                        Resets {new Date(data.resetDate).toLocaleDateString()}
                    </span>
                )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-gray-50 rounded-lg p-3" data-testid="allowance-total">
                    <p className="text-xs text-gray-500">Total Allowance</p>
                    <p className="text-xl font-bold text-gray-900">{data.allowance}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3" data-testid="allowance-used">
                    <p className="text-xs text-gray-500">Used Calls</p>
                    <p className="text-xl font-bold text-gray-900">{used}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3" data-testid="allowance-remaining">
                    <p className="text-xs text-gray-500">Remaining Calls</p>
                    <p className={`text-xl font-bold ${low ? 'text-amber-600' : 'text-gray-900'}`}>{data.remaining}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3" data-testid="allowance-rate">
                    <p className="text-xs text-gray-500">Overage Rate</p>
                    <p className="text-xl font-bold text-gray-900">
                        {rate == null ? '—' : `${rate.toFixed(1)}%`}
                    </p>
                </div>
            </div>

            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                <div
                    className={`h-2.5 rounded-full ${low ? 'bg-amber-500' : 'bg-green-600'}`}
                    style={{ width: `${usedPct}%` }}
                    data-testid="allowance-bar"
                ></div>
            </div>

            {low && (
                <p
                    className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3"
                    role="status"
                    data-testid="allowance-low-warning"
                >
                    Warning: less than 20% of your allowance remains ({data.remaining} of {data.allowance}).
                </p>
            )}

            {exhausted && (
                <div
                    className="border border-red-200 bg-red-50 rounded-md p-4 mb-4"
                    role="alert"
                    data-testid="allowance-exhausted-prompt"
                >
                    <p className="text-sm font-medium text-red-700">
                        You have used 100% of your service call allowance ({used} of {data.allowance}).
                    </p>
                    <p className="text-xs text-red-600 mt-1">
                        New service calls are blocked until you top up or upgrade.
                        {unitRateCents != null && ` Extra calls cost $${(unitRateCents / 100).toFixed(2)} each.`}
                    </p>
                    <div className="flex items-center gap-3 mt-3 flex-wrap">
                        <label htmlFor="allowance-topup-qty" className="text-xs text-gray-600">
                            Extra calls
                        </label>
                        <input
                            id="allowance-topup-qty"
                            type="number"
                            min={1}
                            max={1000}
                            value={topUpQty}
                            onChange={(e) => setTopUpQty(clampTopUpQuantity(Number(e.target.value)))}
                            data-testid="allowance-topup-quantity"
                            className="w-20 border border-gray-300 rounded-md px-2 py-1 text-sm"
                        />
                        {unitRateCents != null && (
                            <span className="text-sm text-gray-900" data-testid="allowance-topup-total">
                                Total: ${((topUpQty * unitRateCents) / 100).toFixed(2)}
                            </span>
                        )}
                        {popupReady ? (
                            <button
                                onClick={() => startTopUpCheckout(data, topUpQty)}
                                disabled={topUpState === 'loading' || topUpState === 'popup'}
                                data-testid="allowance-exhausted-buy"
                                className="ml-auto inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-60"
                            >
                                <i className="fas fa-bolt mr-2"></i>
                                Buy {topUpQty} extra call{topUpQty === 1 ? '' : 's'} now
                            </button>
                        ) : (
                            /* Fail-closed fallback: unbaked store id or wrong market. */
                            <a
                                href={topUpHref(data.tier)}
                                data-testid="allowance-exhausted-buy-link"
                                className="ml-auto inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                            >
                                <i className="fas fa-bolt mr-2"></i>Buy extra calls
                            </a>
                        )}
                    </div>
                </div>
            )}

            <div className="flex flex-wrap gap-3" data-testid="allowance-ctas">
                {/* Live per-unit figure from the F-175 catalog payload (best
                    effort; absent ⇒ hidden, never fabricated). */}
                {unitRateCents != null && (
                    <span
                        className="w-full text-xs text-gray-500"
                        data-testid="allowance-topup-rate"
                    >
                        Extra service calls: ${(unitRateCents / 100).toFixed(2)} per unit.
                    </span>
                )}
                {popupReady ? (
                    <button
                        onClick={() => startTopUpCheckout(data, topUpQty)}
                        disabled={topUpState === 'loading' || topUpState === 'popup'}
                        data-testid="allowance-topup-button"
                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60"
                    >
                        <i className="fas fa-plus-circle mr-2"></i>
                        {topUpState === 'idle' && 'Top up allowance'}
                        {topUpState === 'loading' && 'Preparing checkout…'}                        {topUpState === 'popup' && 'Complete checkout in the popup…'}
                        {topUpState === 'error' && 'Top up unavailable — click to retry'}
                    </button>
                ) : (
                    /* Fail-closed fallback: unbaked store id or wrong market —
                       keep the T-146-002 v1 link behaviour. */
                    <a
                        href={topUpHref(data.tier)}
                        data-testid="allowance-topup-link"
                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                        <i className="fas fa-plus-circle mr-2"></i>Top up allowance
                    </a>
                )}
                {next && (
                    <a
                        href={upgradeHref(next)}
                        data-testid="allowance-upgrade-link"
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                    >
                        <i className="fas fa-arrow-up mr-2"></i>Upgrade plan
                    </a>
                )}
            </div>

            {topUpState === 'error' && topUpMsg && (
                <p className="mt-3 text-xs text-amber-700" role="alert" data-testid="allowance-topup-error">
                    {topUpMsg}
                </p>
            )}
        </div>
    );
};

export default AllowanceWidget;
